import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import {
  DEFAULT_PROSPECT_PROFILE_FIELDS,
  ProspectProfileFieldContext,
  ProspectProfileFieldInstance,
  filterProspectProfileFields,
  getFieldValueFromData,
  getFieldSearchValue,
  getAttributeValue,
  convertCustomFieldsToProspectFields,
  formatDateForApi,
} from '../helpers/prospect-fields';
import {
  getNodeText,
  renderFieldValue,
} from '../helpers/prospect-field-renderers';
import {
  getSingleProspectPinnedFields,
  PinUnpinActionType,
} from '../../../../../../types';
import { RootState } from '../../../../../../../../store/root-reducer';
import { UseProspectProfileFieldsParams } from './types';
import { executeOnRequestStatus } from '../../../../../../../../shared/utils';
import { currencySymbolMap } from '../../../../../prospect-table/utils/helper';
import toaster, { Theme } from '../../../../../../../../shared/toaster';
import { FieldType } from '../../../../../../../settings/enums/field';

const DEFAULT_FIELD_PIN_KEY_MAP: Record<string, string> = {
  // email: 'email',
  'latest-status': 'prospectStatus',
  'last-updated': 'lastUpdated',
  'creation-date': 'creationDate',
  'prospect-outcome': 'prospectOutcomes',
  'sequence-associated': 'sequenceAssociated',
  subsequence: 'subSequenceAssociated',
  'client-associated': 'clientAssociated',
  'prospect-tags': 'prospectTags',
  'last-contacted': 'lastContacted',
  'recently-open-date': 'recentlyOpenDate',
  'recently-replied-date': 'recentlyReplyDate',
  'recently-clicked-date': 'recentlyClickDate',
  'prospect-owner': 'prospectOwner',
  'email-verification-status': 'verificationStatus',
  'email-verification-sub-status': 'verificationSubStatus',
  'free-email-account': 'freeEmailAccount',
  'mx-record': 'mxRecord',
  'creation-source': 'creationSource',
  'email-service-provider': 'emailServiceProvider',
  'number-of-opens': 'numberOfOpens',
  'number-of-clicks': 'numberOfClicks',
  'number-of-email-sent': 'numberOfEmailSent',
};

// Maps field label slugs coming from the Fields API back to our UI field IDs
// when the slug differs from the UI id (e.g. "Phone Number" -> "phone").
const FIELD_LABEL_TO_UI_ID_MAP: Record<string, string> = {
  'phone-number': 'phone',
};

const slugifyLabel = (label?: string | null) =>
  (label || '')
    .trim()
    .toLowerCase()
    .replace(/^-+|-+$/g, '');

// Single configuration for all editable text fields
// To add a new editable field, just add it to this map (fieldId -> label)
export const EDITABLE_FIELDS_CONFIG: Record<string, string> = {
  'job-title': 'Job Title',
  department: 'Department',
  industry: 'Industry',
  experience: 'Experience',
  linkedIn: 'LinkedIn',
  twitter: 'Twitter',
  facebook: 'Facebook',
  website: 'Website',
  city: 'City',
  country: 'Country',
  state: 'State',
  company: 'Company',
  'company-industry': 'Company Industry',
  'company-website': 'Company Website',
  'company-size': 'Company Size',
  'company-revenue': 'Company Revenue',
  'company-founded-year': 'Company Founded Year',
  'company-linkedIn': 'Company LinkedIn',
  'company-phone': 'Company Phone',
};

const useWindowWidth = () => {
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const handleResize = () => {
      // Clear previous timeout
      clearTimeout(timeoutId);
      // Debounce resize handler - only update after 150ms of no resize events
      timeoutId = setTimeout(() => {
        setWidth(window.innerWidth);
      }, 150);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return width;
};


export const useProspectProfileFields = ({
  prospectOutcomeSequences,
  prospectOutcomeSubsequences,
  selectedProspectId,
  allOutcomes,
  prospectData,
  getSinglePropsectDetailPinnedFieldsRequest,
  updateProspectPinStatusRequest,
  sendUpdateProspectFieldByIdRequest,
  fields,
  sendGetSingleProspectDetailsRequest,
  resetUpdateProspectFieldByIdRequest,
  setActiveTab,
  changeTab,
  onActivityChange,
}: UseProspectProfileFieldsParams) => {
  const [prospectFieldSearch, setProspectFieldSearch] = useState<string | null>(
    '',
  );
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [pinnedFieldIds, setPinnedFieldIds] = useState<
    getSingleProspectPinnedFields[] | null
  >(null);
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);

  const getFieldKeyForApi = useCallback(
    (id: string) => {
      const normalize = (value: string | undefined | null) =>
        (value || '')
          .trim()
          .toLowerCase()
          .replace(/[-_]/g, ' ')
          .replace(/\s+/g, ' ');
      const normalizedId = normalize(id);

      // 1) If this id has a predefined mapping, always use that key
      if (DEFAULT_FIELD_PIN_KEY_MAP[id]) {
        return DEFAULT_FIELD_PIN_KEY_MAP[id];
      }

      // 2) Otherwise try to resolve using fields api (id match or label match)
      const matchedField = fields?.find((fieldItem) => {
        const label = normalize(fieldItem.label)?.toLowerCase();
        const slug = slugifyLabel(fieldItem.label)?.toLowerCase();
        const nid = normalizedId?.toLowerCase();

        return (
          String(fieldItem.id) === String(id) ||
          label === nid ||
          slug === nid ||
          (nid === 'phone' && label === 'phone number')
        );
      });

      if (matchedField) {
        return String(matchedField.id);
      }

      // 3) Fallback to the raw id
      return id;
    },
    [fields],
  );

  const screenWidth = useWindowWidth();

  const getUiFieldIdFromApiKey = useCallback(
    (apiKey: string) => {
      // If API returned numeric id, map it back using fields list
      const matchedField = fields?.find(
        (fieldItem) => String(fieldItem.id) === String(apiKey),
      );
      if (matchedField) {
        const slug = slugifyLabel(matchedField.label) || String(apiKey);
        return FIELD_LABEL_TO_UI_ID_MAP[slug] || slug;
      }

      const mappedEntry = Object.entries(DEFAULT_FIELD_PIN_KEY_MAP).find(
        ([, value]) => value === apiKey,
      );
      return mappedEntry ? mappedEntry[0] : apiKey;
    },
    [fields],
  );

  const getSingleProspectPinnedFieldsRes = useSelector(
    (state: RootState) => state?.prospect?.getSingleProspectPinnedFields,
  );
  const saveContactAttributesRequestStatus = useSelector(
    (state: RootState) => state?.prospect?.saveContactAttributesRequest?.status,
  );
  const updateProspectFieldByIdRequestStatus = useSelector(
    (state: RootState) =>
      state?.prospect?.updateProspectFieldByIdRequest?.status,
  );

  const updateProspectFieldByIdRequestMessage = useSelector(
    (state: RootState) =>
      state?.prospect?.updateProspectFieldByIdRequest?.message,
  );

  const userTimezone = useSelector(
    (state: RootState) => state?.myProfile?.myProfile?.timeZone,
  );
  const isEmailFieldHovered = useSelector(
    (state: RootState) => state?.prospect?.isEmailFieldHovered,
  );

  const dealValueData = getAttributeValue(
    prospectData?.attributes,
    'Deal Value',
  );

  const currencyFieldData = fields?.find(
    (fieldItem) => fieldItem?.label === 'Deal Value',
  )?.fieldMetadata?.metadata?.currencyCode;
  // Create a map of field ID to currency symbol for each currency field
  const currencySymbolMapByFieldId = useMemo(() => {
    const map: Record<string, string> = {};
    if (!fields) return map;

    fields
      .filter((fieldItem) => fieldItem?.fieldType === 'currency')
      .forEach((fieldItem) => {
        const fieldId = String(fieldItem.id);
        const currencyCode = fieldItem?.fieldMetadata?.metadata?.currencyCode;
        const symbol = currencyCode
          ? currencySymbolMap[currencyCode] || '$'
          : '$';
        map[fieldId] = symbol;
      });

    return map;
  }, [fields]);

  // Create a map of field ID to currency code for each currency field
  const currencyCodeMapByFieldId = useMemo(() => {
    const map: Record<string, string | undefined> = {};
    if (!fields) return map;

    fields
      .filter((fieldItem) => fieldItem?.fieldType === 'currency')
      .forEach((fieldItem) => {
        const fieldId = String(fieldItem.id);
        const currencyCode = fieldItem?.fieldMetadata?.metadata?.currencyCode;
        map[fieldId] = currencyCode;
      });

    return map;
  }, [fields]);

  // Get currency symbol function - returns symbol for a specific field ID
  const getCurrencySymbol = useCallback(
    (fieldId: string): string => currencySymbolMapByFieldId[fieldId] || '$',
    [currencySymbolMapByFieldId],
  );

  // Get currency code function - returns currency code for a specific field ID
  const getCurrencyCode = useCallback(
    (fieldId: string): string | undefined => currencyCodeMapByFieldId[fieldId],
    [currencyCodeMapByFieldId],
  );

  // Keep backward compatibility for deal-value field (which uses the first currency code)
  const currencySymbol = useMemo(() => {
    if (!dealValueData) return null;
    return currencySymbolMap[currencyFieldData] || '$';
  }, [dealValueData]);

  const closingDateValue = getAttributeValue(
    prospectData?.attributes,
    'Closing Date',
  );

  const [dealValue, setDealValue] = useState<number>(Number(dealValueData));

  const [closingDate, setClosingDate] = useState<Date | null>(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // Initialize prospectOverviewValue from prospectData
  const initialOverviewValue = useMemo(() => {
    if (prospectData?.prospectOverview) {
      return String(prospectData.prospectOverview);
    }
    // Try to get from attributes
    if (prospectData?.attributes) {
      const overviewAttr = prospectData.attributes.find(
        (attr: any) => attr.fieldRef?.label?.trim() === 'Prospect Overview',
      );
      if (overviewAttr?.attributeValue) {
        return String(overviewAttr.attributeValue);
      }
    }
    return '';
  }, [prospectData]);

  const [prospectOverviewValue, setProspectOverviewValue] = useState<string>(
    initialOverviewValue,
  );
  const [prospectOverviewDraft, setProspectOverviewDraft] = useState<string>(
    initialOverviewValue,
  );
  const [pendingOverviewValue, setPendingOverviewValue] = useState<
    string | null
  >(null);

  // Helper to get field label by field ID
  const getFieldLabelById = useCallback(
    (fieldId: string): string => EDITABLE_FIELDS_CONFIG[fieldId] || fieldId,
    [],
  );

  // Initialize editable field values from prospectData
  const initialEditableFieldValues = useMemo(() => {
    const values: Record<string, string> = {};
    if (!prospectData?.attributes) return values;

    // Map each field ID to its value from prospectData attributes for static fields
    Object.entries(EDITABLE_FIELDS_CONFIG).forEach(([fieldId, label]) => {
      const attr = prospectData.attributes.find(
        (a: any) => a?.fieldRef?.label?.trim() === label?.trim(),
      );
      if (attr?.attributeValue) {
        values[fieldId] = String(attr.attributeValue);
      }
    });

    // Also initialize custom fields from attributes
    if (fields && fields.length > 0) {
      fields.forEach((field) => {
        const fieldId = String(field.id);
        const attr = prospectData.attributes.find(
          (a: any) => a?.fieldRef?.label?.trim() === field.label?.trim(),
        );
        if (attr?.attributeValue) {
          values[fieldId] = String(attr.attributeValue);
        }
      });
    }

    return values;
  }, [prospectData, fields]);

  const [editableFieldValues, setEditableFieldValues] = useState<
    Record<string, string>
  >(initialEditableFieldValues);

  // Store initial values when editing starts (for cancel functionality)
  const [editingFieldInitialValues, setEditingFieldInitialValues] = useState<
    Record<string, string>
  >({});

  // Update editable field values when prospectData changes
  useEffect(() => {
    setEditableFieldValues(initialEditableFieldValues);
  }, [initialEditableFieldValues]);

  // Update state when prospectData changes
  useEffect(() => {
    const newDealValue = Number(dealValueData);
    setDealValue(newDealValue);
  }, [prospectData?.attributes]);

  useEffect(() => {
    if (closingDateValue && closingDateValue !== '') {
      const date = new Date(closingDateValue);
      // Check if date is valid
      if (!isNaN(date.getTime())) {
        setClosingDate(date);
      } else {
        setClosingDate(null);
      }
    } else {
      setClosingDate(null);
    }
  }, [closingDateValue]);

  useEffect(() => {
    if (initialOverviewValue !== null && initialOverviewValue !== undefined) {
      setProspectOverviewValue(initialOverviewValue);
      if (editingFieldId !== 'prospect-overview') {
        setProspectOverviewDraft(initialOverviewValue);
      }
    }
  }, [initialOverviewValue, editingFieldId]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as HTMLElement;

      if (
        target.closest(
          '.bs-input, .bs-input-number, .bs-select-box, .prospect-profile-field-check-icon-dropdown, .prospect-profile-field-check-icon, .bs-date-picker, .react-datepicker-popper, .react-datepicker-wrapper, .transfer-overlay, .bs-popover-inner',
        )
      ) {
        return;
      }

      setShowDropdown(false);
      if (editingFieldId) {
        setEditableFieldValues(initialEditableFieldValues);
      }
      setEditingFieldId(null);
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown, editingFieldId, initialEditableFieldValues]);

  // Fetch pinned fields on mount
  useEffect(() => {
    if (getSingleProspectPinnedFieldsRes) {
      // API returns array of objects in payload
      const pinnedFields = Array.isArray(getSingleProspectPinnedFieldsRes)
        ? getSingleProspectPinnedFieldsRes
        : [];

      // Sort by createdAt asc so first pinned appear first
      const sortedPinnedFields = [...pinnedFields].sort((a, b) => {
        const aTime = new Date(a?.createdAt || '').getTime() || 0;
        const bTime = new Date(b?.createdAt || '').getTime() || 0;
        return aTime - bTime;
      });

      setPinnedFieldIds(sortedPinnedFields);
    } else {
      getSinglePropsectDetailPinnedFieldsRequest();
    }
  }, [getSingleProspectPinnedFieldsRes]);

  useEffect(() => {
    executeOnRequestStatus({
      status: updateProspectFieldByIdRequestStatus,
      onSuccess: () => {
        resetUpdateProspectFieldByIdRequest();
        onActivityChange?.(true);
      },
      onFailed: () => {
        resetUpdateProspectFieldByIdRequest();
      },
    });
  }, [updateProspectFieldByIdRequestStatus]);
  // Update prospectOverviewValue only when save is successful
  useEffect(() => {
    executeOnRequestStatus({
      status: saveContactAttributesRequestStatus,
      onSuccess: () => {
        if (pendingOverviewValue !== null) {
          setProspectOverviewValue(pendingOverviewValue);
          // setProspectOverviewDraft(pendingOverviewValue);
          setPendingOverviewValue(null);
        }
      },
      onFailed: () => {
        // On failure, revert draft to current saved value
        // setProspectOverviewDraft(prospectOverviewValue);
        setPendingOverviewValue(null);
      },
    });
  }, [
    saveContactAttributesRequestStatus,
    pendingOverviewValue,
    prospectOverviewValue,
  ]);

  const extractTextFromNode = (node: ReactNode): string =>
    getNodeText(node).trim();

  // Helper function to find fieldRef ID by label
  const getFieldRefIdByLabel = useCallback(
    (label: string): number | null => {
      if (!prospectData?.attributes) return null;
      const attr = prospectData.attributes.find(
        (a: any) => a?.fieldRef?.label?.trim() === label.trim(),
      );
      return attr?.fieldRef?.id ?? null;
    },
    [prospectData?.attributes],
  );

  // Helper function to save field via API
  const saveFieldValue = useCallback(
    (fieldLabel: string, attributeValue: any): Promise<void> => {
      if (!sendUpdateProspectFieldByIdRequest) return;

      const fieldRefId = getFieldRefIdByLabel(fieldLabel);
      if (!fieldRefId) {
        return;
      }
      sendUpdateProspectFieldByIdRequest({
        contactId: selectedProspectId,
        attributeValue,
        fieldRef: fieldRefId,
      });
    },
    [
      sendUpdateProspectFieldByIdRequest,
      getFieldRefIdByLabel,
      selectedProspectId,
    ],
  );

  // Callbacks for field save operations
  const handleClosingDateChange = useCallback(
    async (date: Date | null) => {
      setClosingDate(date);
      // Save closing date via API
      const dateString = date ? date.toLocaleDateString('en-CA') : '';
      saveFieldValue('Closing Date', dateString);
    },
    [saveFieldValue],
  );

  const handleEditEnd = useCallback(async () => {
    // Save field value when editing ends (capture current editingFieldId before clearing)
    const currentEditingFieldId = editingFieldId;
    if (!currentEditingFieldId) return;

    setEditingFieldId(null);

    if (currentEditingFieldId === 'deal-value') {
      // Special handling for deal value (number type)
      // Only save if value actually changed
      const initialDealValue = Number(dealValueData) || 0;
      if (dealValue !== initialDealValue) {
        saveFieldValue('Deal Value', dealValue);
      }
    } else if (
      currentEditingFieldId &&
      currentEditingFieldId in EDITABLE_FIELDS_CONFIG
    ) {
      // Handle all editable text fields using the configuration
      // Check if the field has been edited and value changed
      if (currentEditingFieldId in editableFieldValues) {
        const fieldValue = editableFieldValues[currentEditingFieldId];
        const initialValue =
          editingFieldInitialValues[currentEditingFieldId] ?? '';
        // Only save if value actually changed
        if (fieldValue !== initialValue) {
          const fieldLabel = getFieldLabelById(currentEditingFieldId);
          // Allow empty strings to be saved (to clear the field)
          saveFieldValue(fieldLabel, fieldValue);
        }
      }
    } else if (
      currentEditingFieldId &&
      editableFieldValues[currentEditingFieldId] !== undefined
    ) {
      // Handle custom fields - find the field by ID and get its label
      const customField = fields?.find(
        (f) => String(f.id) === String(currentEditingFieldId),
      );
      if (customField && customField.label) {
        let fieldValue = editableFieldValues[currentEditingFieldId];
        const initialValue =
          editingFieldInitialValues[currentEditingFieldId] ?? '';
        // Only save if value actually changed
        if (fieldValue !== initialValue) {
          // Format date fields to YYYY-MM-DD format before saving
          if (customField.fieldType === FieldType.Date) {
            fieldValue = formatDateForApi(fieldValue);
          }
          // Allow empty strings to be saved (to clear the field)
          saveFieldValue(customField.label, fieldValue);
        }
      }
    }
  }, [
    editingFieldId,
    dealValue,
    dealValueData,
    editableFieldValues,
    editingFieldInitialValues,
    getFieldLabelById,
    saveFieldValue,
    fields,
  ]);

  const handleEditCancel = useCallback(() => {
    // Revert editable field values to initial values when editing is cancelled
    const currentEditingFieldId = editingFieldId;
    if (currentEditingFieldId) {
      if (currentEditingFieldId === 'deal-value') {
        setDealValue(dealValue);
      } else {
        const initialValue =
          editingFieldInitialValues[currentEditingFieldId] ?? '';
        setEditableFieldValues((prev) => ({
          ...prev,
          [currentEditingFieldId]: initialValue,
        }));
      }
    }
    setShowDropdown(false);
    setEditingFieldId(null);
  }, [editingFieldId, editingFieldInitialValues]);

  const handleProspectOverviewSave = useCallback(
    async (value: string) => {
      // Store the pending value - will be set only on successful save
      setPendingOverviewValue(value);
      // setProspectOverviewDraft(value); // Update draft for immediate UI feedback
      setEditingFieldId(null);
      // Save prospect overview via API
      saveFieldValue('Prospect Overview', value);
    },
    [saveFieldValue],
  );

  const handleEditClick = useCallback(
    (fieldId: string) => {
      // Store the current value as initial value when editing starts
      if (fieldId === 'deal-value') {
        setDealValue(dealValue);
      } else {
        // Check if field has been edited, otherwise get from initial values
        let currentValue = '';
        if (fieldId in editableFieldValues) {
          currentValue = editableFieldValues[fieldId];
        } else if (fieldId in initialEditableFieldValues) {
          currentValue = initialEditableFieldValues[fieldId];
        }

        // Initialize the field in editableFieldValues if it doesn't exist yet
        if (!(fieldId in editableFieldValues)) {
          setEditableFieldValues((prev) => ({
            ...prev,
            [fieldId]: currentValue,
          }));
        }

        setEditingFieldInitialValues((prev) => ({
          ...prev,
          [fieldId]: currentValue,
        }));
      }
      setShowDropdown(true);
      setEditingFieldId(fieldId);
      if (fieldId === 'closing-date') {
        setDatePickerOpen(true);
      }
    },
    [editableFieldValues, initialEditableFieldValues, dealValue],
  );

  const handleEditableFieldChange = useCallback(
    (fieldId: string, value: string) => {
      setEditableFieldValues((prev) => ({
        ...prev,
        [fieldId]: value,
      }));
    },
    [],
  );

  const context: ProspectProfileFieldContext = useMemo(
    () => ({
      prospectOutcomeSequences,
      prospectOutcomeSubsequences,
      selectedProspectId,
      allOutcomes,
      prospectFieldSearch,
      onProspectFieldSearch: (value: string) => setProspectFieldSearch(value),
      setActiveTab,
      changeTab,
    }),
    [
      prospectOutcomeSequences,
      prospectOutcomeSubsequences,
      selectedProspectId,
      allOutcomes,
      prospectFieldSearch,
      setActiveTab,
      changeTab,
    ],
  );

  // Convert custom fields to ProspectProfileField format
  const customProspectFields = useMemo(() => {
    if (!fields || fields.length === 0) {
      return [];
    }
    const staticFieldIds = DEFAULT_PROSPECT_PROFILE_FIELDS.map((f) => f.id);
    return convertCustomFieldsToProspectFields(fields, staticFieldIds);
  }, [fields]);

  // Merge static and custom fields
  const allFields = useMemo(
    () => [...DEFAULT_PROSPECT_PROFILE_FIELDS, ...customProspectFields],
    [customProspectFields],
  );
  // Memoize base field data (without valueNodes) - this rarely changes
  const baseFieldsWithDynamicValues = useMemo(
    () =>
      allFields?.map((field, index) => {
        // Get dynamic value from API data (or use static fallback)
        const dynamicValue = getFieldValueFromData(
          field.id,
          prospectData,
          fields,
          userTimezone,
          screenWidth,
          isEmailFieldHovered,
        );
        const dynamicSearchValue = getFieldSearchValue(field.id, prospectData);

        return {
          ...field,
          value: dynamicValue ?? (field.value || '-'), // Use API data if available, else static
          searchValue: dynamicSearchValue || field.searchValue,
          order: index,
        };
      }),
    [
      prospectData,
      allFields,
      fields,
      userTimezone,
      screenWidth,
      isEmailFieldHovered,
    ],
  );

  // Memoize callbacks to prevent unnecessary re-renders
  const onProspectOverviewCancel = useCallback(() => {
    // setProspectOverviewDraft(prospectOverviewValue);
    setEditingFieldId(null);
  }, [prospectOverviewValue]);

  // Memoize render params that don't change on every keystroke
  const stableRenderParams = useMemo(
    () => ({
      context,
      onDealValueChange: setDealValue,
      onClosingDateChange: handleClosingDateChange,
      onEditEnd: handleEditEnd,
      onDatePickerOpenChange: setDatePickerOpen,
      onProspectOverviewChange: setProspectOverviewDraft,
      onProspectOverviewSave: handleProspectOverviewSave,
      saveContactAttributesRequestStatus,
      onProspectOverviewCancel,
      onEditClick: handleEditClick,
      onEditableFieldChange: handleEditableFieldChange,
      onEditCancel: handleEditCancel,
      currencyFieldData,
    }),
    [
      context,
      handleClosingDateChange,
      handleEditEnd,
      handleProspectOverviewSave,
      saveContactAttributesRequestStatus,
      onProspectOverviewCancel,
      handleEditClick,
      handleEditableFieldChange,
      handleEditCancel,
      currencyFieldData,
    ],
  );

  // Create a serialized version of editableFieldValues for the editing field only
  // This allows us to only trigger recalculation when the editing field changes
  const editingFieldValue = useMemo(() => {
    if (!editingFieldId) return null;
    return editableFieldValues[editingFieldId] ?? null;
  }, [editingFieldId, editableFieldValues]);

  // Memoize valueNodes - optimized to reduce recalculations
  // The key optimization: editableFieldValues is only used for the editing field
  // Other fields use their cached values, so we only need to track the editing field's value
  const prospectProfileFields: ProspectProfileFieldInstance[] = useMemo(
    () =>
      baseFieldsWithDynamicValues?.map((field) => {
        const valueNode = renderFieldValue({
          field,
          ...stableRenderParams,
          editingFieldId,
          dealValue,
          currencySymbol,
          getCurrencySymbol,
          getCurrencyCode,
          closingDate,
          datePickerOpen,
          prospectOverviewValue,
          prospectOverviewDraft,
          editableFieldValues, // Pass full object, but memoization is optimized by editingFieldValue
          showDropdown,
        });

        return {
          ...field,
          valueNode,
        };
      }) || [],
    [
      baseFieldsWithDynamicValues,
      stableRenderParams,
      editingFieldId,
      editingFieldValue, // Only changes when the editing field's value changes
      dealValue,
      closingDate,
      datePickerOpen,
      prospectOverviewValue,
      prospectOverviewDraft,
      getCurrencySymbol,
      getCurrencyCode,
      showDropdown,
    ],
  );

  useEffect(() => {
    if (!prospectOverviewValue) {
      const overviewField = prospectProfileFields.find(
        (field) => field.id === 'prospect-overview',
      );
      const fallbackValue = overviewField
        ? extractTextFromNode(overviewField.valueNode)
        : '';
      if (fallbackValue) {
        // setProspectOverviewValue(fallbackValue);
      }
    }
  }, [prospectProfileFields, prospectOverviewValue]);

  useEffect(() => {
    if (editingFieldId === 'prospect-overview') {
      // Always initialize draft with the current saved value when opening editor
      // Filter out hyphen "-" which is used for display purposes only
      const draftValue =
        prospectOverviewValue && prospectOverviewValue.trim() !== '-'
          ? prospectOverviewValue
          : '';
      setProspectOverviewDraft(draftValue);
    }
  }, [editingFieldId, prospectOverviewValue]);

  const filteredProspectProfileFields = useMemo(() => {
    // Extract just the fieldKey strings from the array of objects
    const fieldKeys =
      pinnedFieldIds?.map((item) => getUiFieldIdFromApiKey(item.fieldKey)) ||
      [];
    return filterProspectProfileFields(
      prospectProfileFields,
      prospectFieldSearch,
      fieldKeys,
    );
  }, [
    prospectProfileFields,
    prospectFieldSearch,
    pinnedFieldIds,
    getUiFieldIdFromApiKey,
  ]);

  const handlePinToggle = async (fieldId: string) => {
    const fieldKeyForApi = getFieldKeyForApi(fieldId);

    const isPinned = pinnedFieldIds?.some(
      (item) => item.fieldKey === fieldKeyForApi,
    );
    const action = isPinned ? PinUnpinActionType.UnPin : PinUnpinActionType.Pin;

    // Optimistic update
    setPinnedFieldIds((previous) => {
      if (!previous) {
        // If no previous data, create new array with the field
        return [
          {
            userId: 0, // Will be updated from API response
            fieldKey: fieldKeyForApi,
            createdAt: new Date().toISOString(),
          },
        ];
      }

      const fieldExists = previous.some(
        (item) => item.fieldKey === fieldKeyForApi,
      );

      if (fieldExists) {
        // Remove the field
        return previous.filter((item) => item.fieldKey !== fieldKeyForApi);
      }
      // Add the field to the end (will be sorted by createdAt ASC)
      return [
        ...previous,
        {
          userId: 0, // Will be updated from API response
          fieldKey: fieldKeyForApi,
          createdAt: new Date().toISOString(),
        },
      ];
    });

    updateProspectPinStatusRequest({
      action,
      fieldKey: fieldKeyForApi,
    });
    // Refresh the pinned fields list after successful update
  };

  const handlePinKeyDown = (
    event: React.KeyboardEvent<HTMLSpanElement>,
    fieldId: string,
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handlePinToggle(fieldId);
    }
  };

  const handleEditKeyDown = (
    event: React.KeyboardEvent<HTMLSpanElement>,
    fieldId: string,
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleEditClick(fieldId);
    }
  };

  return {
    prospectFieldSearch,
    setProspectFieldSearch,
    pinnedFieldIds:
      pinnedFieldIds?.map((item) => ({
        ...item,
        fieldKey: getUiFieldIdFromApiKey(item.fieldKey),
      })) || null,
    editingFieldId,
    filteredProspectProfileFields,
    handlePinToggle,
    handlePinKeyDown,
    handleEditClick,
    handleEditKeyDown,
  };
};
